// Executive Summary Generator
// Produces a construction-knowledge-aware narrative from DCMA scoring results.
// Critical-path findings are always prioritized over non-critical-path issues.

import type { DCMAScoreResult, CriterionResult } from './dcma-scoring-engine';

interface SummarySection {
  heading: string;
  paragraphs: string[];
  severity: 'critical' | 'warning' | 'info';
}

export interface ExecutiveSummary {
  overallAssessment: string;
  sections: SummarySection[];
  recommendations: string[];
  closingStatement: string;
}

// ── Helpers ────────────────────────────────────────────────────────

function getCriterion(results: CriterionResult[], id: string): CriterionResult | undefined {
  return results.find((r) => r.criterionId === id);
}

function isBad(cr: CriterionResult | undefined): boolean {
  if (!cr) return false;
  return cr.tier === 'bad' || cr.tier === 'badLow' || cr.tier === 'badHigh';
}

function isAcceptable(cr: CriterionResult | undefined): boolean {
  return cr?.tier === 'acceptable';
}

function isGood(cr: CriterionResult | undefined): boolean {
  return cr?.tier === 'good';
}

function pct(cr: CriterionResult | undefined): string {
  return cr ? cr.displayValue : 'N/A';
}

function flagCount(cr: CriterionResult | undefined): number {
  return cr?.flaggedItems.length ?? 0;
}

// ── Main Generator ─────────────────────────────────────────────────

export function generateExecutiveSummary(result: DCMAScoreResult): ExecutiveSummary {
  const { criterionResults, totalScore, grade, summary } = result;
  const sections: SummarySection[] = [];
  const recommendations: string[] = [];

  // ── Overall Assessment ───────────────────────────────────────────

  let overallAssessment: string;

  if (totalScore >= 90) {
    overallAssessment = `This schedule demonstrates strong adherence to DCMA 14-point standards with an overall grade of ${grade} (${totalScore.toFixed(1)}/100). The schedule logic, critical path structure, and relationship network are well-constructed, providing a reliable basis for project forecasting and decision-making. Minor refinements may still be possible as noted below.`;
  } else if (totalScore >= 80) {
    overallAssessment = `This schedule scores a ${grade} (${totalScore.toFixed(1)}/100), indicating generally sound construction but with notable areas requiring attention. While the schedule can serve as a working management tool, the issues identified below could reduce forecast accuracy and should be addressed to prevent schedule erosion during execution.`;
  } else if (totalScore >= 70) {
    overallAssessment = `With a grade of ${grade} (${totalScore.toFixed(1)}/100), this schedule has significant quality issues that compromise its reliability as a project management tool. The findings below identify structural problems that, if left unaddressed, will likely lead to inaccurate forecasts, missed early warnings, and difficulty substantiating time-impact claims.`;
  } else if (totalScore >= 60) {
    overallAssessment = `This schedule receives a ${grade} (${totalScore.toFixed(1)}/100), reflecting substantial deficiencies in schedule construction. Multiple critical quality metrics fall outside acceptable DCMA thresholds. In its current state, this schedule cannot reliably forecast project completion or support delay analysis. Immediate remediation is strongly recommended before using this schedule for any contractual or management purposes.`;
  } else {
    overallAssessment = `This schedule scores ${grade} (${totalScore.toFixed(1)}/100), indicating fundamental structural problems that render it unreliable for project control. The schedule requires a comprehensive rebuild of its logic network, constraint structure, and activity definitions before it can serve as a credible management baseline. The issues below are listed in order of impact.`;
  }

  // ── Critical Path Analysis (highest priority) ────────────────────

  const cpPct = getCriterion(criterionResults, 'critical-path-percent');

  if (cpPct) {
    const paragraphs: string[] = [];

    if (cpPct.tier === 'badLow') {
      paragraphs.push(
        `The critical path comprises only ${pct(cpPct)} of incomplete activities, which is well below the recommended 10-20% range. A critical path this thin typically indicates excessive float in the schedule, missing logic ties, or open-ended activities that prevent the network from properly identifying the longest path. This means the schedule will not accurately reflect how delays propagate to the project completion date.`
      );
      paragraphs.push(
        `In construction terms, this is equivalent to having a schedule where most activities appear to have slack -- making it impossible to distinguish which work sequences truly drive the completion date. Schedule-driven decisions such as resource leveling, look-ahead planning, and delay analysis will be unreliable.`
      );
      recommendations.push(
        'Review and tighten logic ties to establish a realistic critical path between 10-20% of activities.'
      );
      sections.push({
        heading: 'Critical Path: Dangerously Thin',
        paragraphs,
        severity: 'critical',
      });
    } else if (cpPct.tier === 'badHigh') {
      paragraphs.push(
        `At ${pct(cpPct)}, the critical path encompasses an unusually high proportion of activities -- well above the recommended 10-20% range. This indicates the schedule has very little float, meaning nearly every activity directly drives the project completion date. Any delay, no matter how minor, will immediately impact the finish date.`
      );
      paragraphs.push(
        `For a construction project, this level of schedule fragility leaves no room for the normal variability inherent in field operations -- weather delays, material lead times, subcontractor coordination, and inspection cycles. The schedule may have excessive hard constraints or missing float buffers that should be investigated.`
      );
      recommendations.push(
        'Investigate why so many activities are on the critical path; check for excessive hard constraints, missing float paths, or unrealistic durations.'
      );
      sections.push({
        heading: 'Critical Path: Overly Fragile Schedule',
        paragraphs,
        severity: 'critical',
      });
    } else if (isAcceptable(cpPct)) {
      paragraphs.push(
        `The critical path at ${pct(cpPct)} is near the optimal range but slightly outside. This is acceptable for planning purposes but should be monitored during execution as the schedule is updated. Minor logic adjustments could bring it into the ideal 10-20% band.`
      );
      sections.push({ heading: 'Critical Path: Near Optimal', paragraphs, severity: 'warning' });
    } else {
      paragraphs.push(
        `The critical path at ${pct(cpPct)} falls within the recommended 10-20% range, indicating a well-balanced schedule where the longest path is clearly defined without being overly sensitive to minor disruptions. This supports reliable forecasting and effective critical path management.`
      );
      sections.push({ heading: 'Critical Path: Well Balanced', paragraphs, severity: 'info' });
    }
  }

  // ── Missing Logic (critical -- affects path integrity) ───────────

  const missingLogic = getCriterion(criterionResults, 'missing-logic');

  if (missingLogic && !isGood(missingLogic)) {
    const count = flagCount(missingLogic);
    const paragraphs: string[] = [];

    if (isBad(missingLogic)) {
      paragraphs.push(
        `${pct(missingLogic)} of activities (${count} total) have missing predecessors or successors. This is a fundamental logic deficiency. Activities without complete logic ties are effectively disconnected from the network -- they float freely and do not respond to delays or acceleration in related work. This directly undermines the integrity of the critical path calculation.`
      );
      paragraphs.push(
        `On a construction project, this means that key work sequences such as concrete pours dependent on rebar installation, or mechanical rough-in dependent on framing completion, may not be logically connected. Schedule updates will not accurately cascade changes through the network.`
      );
      recommendations.push(
        `Establish predecessor and successor relationships for all ${count} activities with missing logic to create a fully connected network.`
      );
    } else {
      paragraphs.push(
        `A small number of activities (${pct(missingLogic)}, ${count} items) have incomplete logic. While not critical, each disconnected activity is a gap in the schedule network that could mask delays.`
      );
      recommendations.push(
        'Close remaining logic gaps to ensure all activities participate in the network calculation.'
      );
    }

    sections.push({
      heading: 'Schedule Logic Completeness',
      paragraphs,
      severity: isBad(missingLogic) ? 'critical' : 'warning',
    });
  }

  // ── Relationship Types ───────────────────────────────────────────

  const relTypes = getCriterion(criterionResults, 'relationship-types');

  if (relTypes && !isGood(relTypes)) {
    const paragraphs: string[] = [];
    const ratio = (
      (summary.fsRelationships / Math.max(summary.totalRelationships, 1)) *
      100
    ).toFixed(0);

    paragraphs.push(
      `Finish-to-Start (FS) relationships account for ${ratio}% of all logic ties, which falls below the recommended threshold. FS relationships represent the most intuitive and auditable logic type in construction scheduling -- "Activity B cannot start until Activity A finishes." Over-reliance on Start-to-Start (SS) or Finish-to-Finish (FF) ties can obscure the true work sequence and make the schedule harder to validate in the field.`
    );

    if (summary.sfRelationships > 0) {
      paragraphs.push(
        `Additionally, ${summary.sfRelationships} Start-to-Finish (SF) relationship${summary.sfRelationships > 1 ? 's were' : ' was'} detected. SF relationships are extremely rare in legitimate construction scheduling and often indicate a modeling error. Each should be reviewed and likely converted to a standard FS or SS relationship.`
      );
      recommendations.push(
        'Eliminate Start-to-Finish (SF) relationships and convert to appropriate FS or SS logic.'
      );
    }

    recommendations.push(
      'Increase the proportion of Finish-to-Start relationships to improve schedule readability and auditability.'
    );
    sections.push({
      heading: 'Relationship Type Distribution',
      paragraphs,
      severity: isBad(relTypes) ? 'critical' : 'warning',
    });
  }

  // ── High Float ───────────────────────────────────────────────────

  const highFloat = getCriterion(criterionResults, 'high-float');

  if (highFloat && !isGood(highFloat)) {
    const paragraphs: string[] = [];

    paragraphs.push(
      `${pct(highFloat)} of activities exhibit total float exceeding 44 working days (${flagCount(highFloat)} activities). High-float activities suggest they are loosely connected to the schedule network. While some float is expected for non-critical work, excessive float often indicates missing logic, overly generous durations, or activities that have been "parked" in the schedule without proper sequencing.`
    );
    paragraphs.push(
      `In a construction context, these activities may represent scope that has been scheduled but not properly integrated into the work flow -- for example, a punch list activity added months before it is relevant, or a procurement task without logic ties to the installation it supports.`
    );
    recommendations.push(
      'Review high-float activities for missing logic ties or unrealistic durations that disconnect them from the work sequence.'
    );

    sections.push({
      heading: 'Excessive Float Activities',
      paragraphs,
      severity: isBad(highFloat) ? 'critical' : 'warning',
    });
  }

  // ── Negative Float ───────────────────────────────────────────────

  const negFloat = getCriterion(criterionResults, 'negative-float');

  if (negFloat && !isGood(negFloat)) {
    const paragraphs: string[] = [];

    paragraphs.push(
      `${pct(negFloat)} of activities show negative total float (${flagCount(negFloat)} activities). Negative float indicates these activities are already behind the schedule's own target dates -- they cannot be completed within the current planned timeframe without acceleration, additional resources, or scope adjustment.`
    );
    paragraphs.push(
      `On a construction project, negative float is a red flag that requires immediate management attention. These activities often reside on or near the critical path, meaning their delay directly impacts the project completion date. Each negative-float chain should be analyzed to determine whether the dates are achievable or whether a recovery plan is needed.`
    );
    paragraphs.push(
      `Planera specializes in helping teams recover from negative float conditions. By automatically analyzing the driving logic path behind each negative-float chain, Planera identifies the most effective acceleration opportunities -- whether through resource augmentation, re-sequencing of non-dependent work, fast-tracking overlapping activities, or eliminating unnecessary constraints. Rather than manually tracing hundreds of logic ties, Planera surfaces the highest-impact recovery actions so your team can focus on execution instead of schedule forensics.`
    );
    recommendations.push(
      'Develop recovery plans for negative-float activity chains, including resource loading and potential re-sequencing.'
    );
    recommendations.push(
      "Use Planera's schedule acceleration engine to automatically identify the most cost-effective paths to eliminate negative float and restore schedule margin."
    );

    sections.push({
      heading: 'Negative Float: Schedule Behind Target',
      paragraphs,
      severity: 'critical',
    });
  }

  // ── Lags ─────────────────────────────────────────────────────────

  const lags = getCriterion(criterionResults, 'lags');

  if (lags && !isGood(lags)) {
    const paragraphs: string[] = [];

    paragraphs.push(
      `${pct(lags)} of relationships use lag values (${flagCount(lags)} relationships). Lags are time delays inserted between related activities -- while sometimes appropriate (e.g., concrete cure time), excessive lag usage often masks missing activities. A 5-day lag between formwork removal and painting might be better represented as an explicit "concrete curing" activity that can be tracked and managed independently.`
    );
    recommendations.push(
      'Replace lags with explicit activities where the lag represents real work or waiting time that should be tracked.'
    );

    sections.push({
      heading: 'Relationship Lags',
      paragraphs,
      severity: isBad(lags) ? 'critical' : 'warning',
    });
  }

  // ── Hard Constraints ─────────────────────────────────────────────

  const constraints = getCriterion(criterionResults, 'number-of-constraints');

  if (constraints && !isGood(constraints)) {
    const paragraphs: string[] = [];

    paragraphs.push(
      `${pct(constraints)} of activities have hard date constraints (Must Start On, Must Finish On, Start/Finish No Later Than) -- ${flagCount(constraints)} activities total. Hard constraints override the schedule's calculated dates and force activities to specific calendar dates regardless of their logical relationships. This reduces the schedule's ability to dynamically respond to changes and can mask the true critical path.`
    );
    paragraphs.push(
      `In construction, hard constraints should be limited to genuinely immovable dates such as contractual milestones, permit deadlines, or third-party delivery dates. If constraints are being used to "fix" schedule logic issues, the underlying logic should be corrected instead.`
    );
    recommendations.push(
      "Review hard constraints and remove any that don't represent genuinely immovable external dates; use logic ties instead."
    );

    sections.push({
      heading: 'Hard Date Constraints',
      paragraphs,
      severity: isBad(constraints) ? 'critical' : 'warning',
    });
  }

  // ── High Duration ────────────────────────────────────────────────

  const highDuration = getCriterion(criterionResults, 'high-duration');

  if (highDuration && !isGood(highDuration)) {
    const paragraphs: string[] = [];

    paragraphs.push(
      `${pct(highDuration)} of activities have durations exceeding 44 working days (${flagCount(highDuration)} activities). Long-duration activities reduce schedule granularity and make it difficult to measure meaningful progress during execution. A 60-day "Install HVAC" activity provides far less management visibility than a sequence of 10-15 day tasks covering ductwork, equipment setting, piping, controls, and testing.`
    );
    recommendations.push(
      'Break down activities longer than 44 working days into smaller, measurable work packages that support meaningful progress tracking.'
    );

    sections.push({
      heading: 'Activity Duration Concerns',
      paragraphs,
      severity: isBad(highDuration) ? 'warning' : 'info',
    });
  }

  // ── Closing Statement ────────────────────────────────────────────

  let closingStatement: string;

  if (totalScore >= 85) {
    closingStatement = `Overall, this schedule is well-constructed and suitable for project execution. Addressing the minor findings above will further strengthen its reliability as the project progresses through construction phases.`;
  } else if (totalScore >= 70) {
    closingStatement = `This schedule requires targeted improvements before it can fully serve as a reliable project management baseline. The recommendations above are listed in priority order -- addressing the critical-path and logic issues first will have the greatest impact on schedule integrity. Planera can help automate these improvements and monitor schedule quality as the project progresses.`;
  } else {
    closingStatement = `This schedule needs significant remediation to meet industry-standard quality thresholds. We recommend prioritizing the critical-path and missing-logic findings, as these have the most direct impact on forecast reliability and delay analysis capability. A schedule in this condition poses material risk to project controls -- Planera's automated quality monitoring can help track improvement and prevent regression as corrections are made.`;
  }

  return {
    overallAssessment,
    sections,
    recommendations,
    closingStatement,
  };
}
